import type { Hotspot } from "../types";
import { feedback } from "../services/feedback";

type HotspotCallbacks = {
  onSelect: (hotspot: Hotspot) => void;
};

export class HotspotOverlay {
  private readonly layer: HTMLDivElement;
  private readonly tooltip: HTMLDivElement;
  private buttons: Array<{
    hotspot: Hotspot;
    button: HTMLButtonElement;
    used: boolean;
  }> = [];
  private container: HTMLElement;
  private hostFrame: number | null = null;

  constructor(container: HTMLElement, private readonly callbacks: HotspotCallbacks) {
    this.container = container;
    this.layer = document.createElement("div");
    this.layer.className = "hotspot-layer";
    this.layer.setAttribute("aria-hidden", "false");
    container.append(this.layer);

    this.tooltip = document.createElement("div");
    this.tooltip.className = "hotspot-tooltip hidden";
    container.append(this.tooltip);

    // Dismiss the tooltip on any click outside of it (or its hotspot pin).
    // Without this it stays pinned over the panorama after the player has
    // moved on to the map/year UI and blocks part of the screen.
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (this.tooltip.classList.contains("hidden")) return;
        const target = event.target as Node | null;
        if (!target) return;
        if (this.tooltip.contains(target)) return;
        if (target instanceof Element && target.closest(".hotspot-pin")) return;
        this.hideTooltip();
      },
      true
    );
  }

  setHotspots(hotspots: Hotspot[]): void {
    this.layer.replaceChildren();
    this.tooltip.classList.add("hidden");
    this.buttons = hotspots.map((hotspot) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "hotspot-pin";
      button.dataset.id = hotspot.id;
      button.setAttribute("aria-label", `Inspect ${hotspot.title}`);
      button.innerHTML = `<span aria-hidden="true">+</span>`;
      // The panorama viewer attaches pointerdown to its container and calls
      // preventDefault() to start a drag — that suppresses the synthetic
      // click on this button. Stop the pointerdown from reaching the viewer
      // so the click fires normally.
      button.addEventListener("pointerdown", (event) => {
        event.stopPropagation();
      });
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        this.activate(hotspot);
      });
      this.layer.append(button);
      return { hotspot, button, used: false };
    });
  }

  clear(): void {
    this.buttons = [];
    this.layer.replaceChildren();
    this.tooltip.classList.add("hidden");
  }

  hideTooltip(): void {
    this.tooltip.classList.add("hidden");
  }

  /**
   * Theatre Mode: pulse a hotspot pin without opening the tooltip — narration
   * scripts trigger visual cues at scripted timestamps.
   */
  pulse(hotspotId: string): void {
    const slot = this.buttons.find((entry) => entry.hotspot.id === hotspotId);
    if (!slot) return;
    slot.button.classList.remove("pulsing");
    void slot.button.offsetWidth;
    slot.button.classList.add("pulsing");
    window.setTimeout(() => slot.button.classList.remove("pulsing"), 1800);
  }

  setVisible(visible: boolean): void {
    this.layer.dataset.visible = String(visible);
  }

  /** Project hotspots onto the screen given current camera lon/lat/fov. */
  update(viewer: { lon: number; lat: number; fov: number; aspect: number }): void {
    if (this.buttons.length === 0) {
      return;
    }

    if (this.hostFrame !== null) {
      cancelAnimationFrame(this.hostFrame);
    }
    this.hostFrame = window.requestAnimationFrame(() => {
      this.hostFrame = null;
      this.project(viewer);
    });
  }

  private activate(hotspot: Hotspot): void {
    const slot = this.buttons.find((entry) => entry.hotspot.id === hotspot.id);
    if (!slot) {
      return;
    }

    slot.used = true;
    slot.button.classList.add("used");
    this.tooltip.innerHTML = `<strong>${escapeHtml(hotspot.title)}</strong><span>${escapeHtml(hotspot.factoid)}</span>`;
    this.tooltip.classList.remove("hidden");
    const rect = slot.button.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();
    const left = rect.left - containerRect.left + rect.width / 2;
    const top = rect.top - containerRect.top - 12;
    this.tooltip.style.left = `${left}px`;
    this.tooltip.style.top = `${top}px`;
    feedback.hotspot();
    this.callbacks.onSelect(hotspot);
  }

  private project(viewer: { lon: number; lat: number; fov: number; aspect: number }): void {
    const containerRect = this.container.getBoundingClientRect();
    const halfFovY = degToRad(viewer.fov / 2);
    const halfFovX = Math.atan(Math.tan(halfFovY) * viewer.aspect);
    const cameraDir = sphericalDirection(viewer.lon, viewer.lat);
    const cameraRight = sphericalDirection(viewer.lon + 90, 0);
    const cameraUp = cross(cameraRight, cameraDir);

    for (const slot of this.buttons) {
      const dir = sphericalDirection(slot.hotspot.yaw, slot.hotspot.pitch);
      const dotForward = dot(dir, cameraDir);
      if (dotForward <= 0.05) {
        slot.button.style.display = "none";
        continue;
      }

      const x = dot(dir, cameraRight) / dotForward;
      const y = dot(dir, cameraUp) / dotForward;
      const screenX = (x / Math.tan(halfFovX) + 1) / 2;
      const screenY = 1 - (y / Math.tan(halfFovY) + 1) / 2;

      if (screenX < -0.05 || screenX > 1.05 || screenY < -0.05 || screenY > 1.05) {
        slot.button.style.display = "none";
        continue;
      }

      slot.button.style.display = "block";
      slot.button.style.left = `${screenX * containerRect.width}px`;
      slot.button.style.top = `${screenY * containerRect.height}px`;
    }
  }
}

function sphericalDirection(yawDeg: number, pitchDeg: number): [number, number, number] {
  const yaw = degToRad(yawDeg);
  const pitch = degToRad(pitchDeg);
  return [
    Math.cos(pitch) * Math.sin(yaw),
    Math.sin(pitch),
    -Math.cos(pitch) * Math.cos(yaw)
  ];
}

function dot(a: [number, number, number], b: [number, number, number]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: [number, number, number], b: [number, number, number]): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}

function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
